import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Copy, ExternalLink, Save, Trash2, RotateCcw, Code2 } from "lucide-react";
import api from "@/lib/api";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import FieldInput from "@/components/admin/crud/FieldInput";
import { StatusPill } from "@/components/admin/crud/CellValue";
import { singular, GROUPS, PROTECTED_FIELDS, groupOf, publicUrlFor, titleOf, fmtDate } from "@/components/admin/crud/crudUtils";

const Section = ({ group, children }) => (
  <section id={`sec-${group.key}`} className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-7 shadow-[0_1px_2px_rgba(7,27,34,0.04)]" data-testid={`edit-section-${group.key}`}>
    <h2 className="font-display text-lg font-semibold text-[#071b22]">{group.label}</h2>
    <p className="text-sm text-slate-500 mt-0.5 mb-5">{group.hint}</p>
    <div className="grid gap-5 md:grid-cols-2">{children}</div>
  </section>
);

function SideCard({ title, children, testid }) {
  return (
    <div className="rounded-3xl border border-slate-200/80 bg-white p-5" data-testid={testid}>
      <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-3">{title}</div>
      {children}
    </div>
  );
}

export default function AdminCRUDEditPage() {
  const { entity, id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [full, setFull] = useState(null);
  const [form, setForm] = useState({});
  const [initial, setInitial] = useState("{}");
  const [saving, setSaving] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [rev, setRev] = useState(0);

  const hydrate = (data) => {
    const f = {};
    Object.keys(data).forEach((k) => { if (!PROTECTED_FIELDS.includes(k)) f[k] = data[k]; });
    setFull(data); setForm(f); setInitial(JSON.stringify(f)); setRev((r) => r + 1);
  };

  useEffect(() => {
    api.get("/admin/crud/entities").then(({ data }) => setMeta((data || []).find((e) => e.key === entity) || false));
    api.get(`/admin/crud/${entity}/${id}`).then(({ data }) => hydrate(data)).catch(() => { toast.error("Record not found"); setFull(false); });
  }, [entity, id]);

  const statusField = meta?.status_field;
  const grouped = useMemo(() => {
    const g = {};
    Object.keys(form).forEach((f) => { if (f === statusField) return; const k = groupOf(f, full?.[f]); (g[k] = g[k] || []).push(f); });
    return g;
  }, [form, full, statusField]);
  const dirty = JSON.stringify(form) !== initial;
  const setField = (f, v) => setForm((p) => ({ ...p, [f]: v === "" && full?.[f] == null ? null : v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/crud/${entity}/${id}`, form);
      hydrate(data && data.id ? data : { ...full, ...form });
      toast.success("Changes saved");
    } catch (err) { toast.error(err?.response?.data?.error || err?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const changeStatus = async (s) => {
    if (!s || s === form[statusField]) return;
    const reason = window.prompt(`Reason for moving to "${s.replace(/_/g, " ")}"? (optional)`);
    if (reason === null) return;
    try {
      await api.post(`/admin/crud/${entity}/${id}/status`, { status: s, reason: reason || undefined });
      setForm((p) => ({ ...p, [statusField]: s }));
      setInitial((prev) => JSON.stringify({ ...JSON.parse(prev), [statusField]: s }));
      toast.success("Status updated");
    } catch (err) { toast.error(err?.response?.data?.error || "Status change failed"); }
  };
  const trash = async () => {
    if (!window.confirm("Move this record to trash? You can restore it later.")) return;
    try { await api.delete(`/admin/crud/${entity}/${id}`); toast.success("Moved to trash"); navigate(`/admin/entities/${entity}`); }
    catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const restore = async () => {
    try { await api.post(`/admin/crud/${entity}/${id}/restore`); toast.success("Restored"); api.get(`/admin/crud/${entity}/${id}`).then(({ data }) => hydrate(data)); }
    catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  if (full === null || meta === null) return <div className="p-10 text-slate-500" data-testid="crud-edit-loading">Loading record…</div>;
  if (full === false || meta === false) return <div className="p-10 text-slate-500" data-testid="crud-edit-missing">This record isn't available. <Link className="text-[#165a71] underline" to={`/admin/entities/${entity}`}>Back to list</Link></div>;

  const viewUrl = publicUrlFor(entity, full);
  const mainGroups = GROUPS.filter((g) => g.key !== "advanced" && grouped[g.key]?.length);
  const advanced = grouped.advanced || [];

  return (
    <div className="max-w-6xl pb-28" data-testid="crud-edit-page">
      <Link to={`/admin/entities/${entity}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#165a71] transition-colors" data-testid="crud-edit-back"><ArrowLeft className="h-4 w-4" /> All {meta.label.toLowerCase()}</Link>

      <div className="mt-4 mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Edit {singular(meta.label)}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22] truncate max-w-[42rem]" data-testid="crud-edit-title">{titleOf({ ...full, ...form })}</h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {statusField && <StatusPill value={form[statusField]} testid="crud-edit-status-pill" />}
            {full.deleted_at && <span className="rounded-full bg-rose-50 text-rose-700 px-2.5 py-0.5 text-[11px] font-semibold">In trash</span>}
            <button onClick={() => { navigator.clipboard?.writeText(id); toast.success("ID copied"); }} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-mono text-slate-500 hover:bg-slate-200" data-testid="crud-edit-copy-id"><Copy className="h-3 w-3" /> {id.slice(0, 12)}</button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {viewUrl && <a href={viewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-[#071b22] hover:border-[#165a71]" data-testid="crud-edit-view"><ExternalLink className="h-4 w-4" /> View live</a>}
          <button onClick={save} disabled={saving || !dirty} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:bg-[#0f4657] disabled:opacity-40 transition-colors" data-testid="crud-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] items-start">
        <div className="space-y-6">
          {mainGroups.map((g) => (
            <Section key={g.key} group={g}>
              {grouped[g.key].map((f) => <FieldInput key={`${f}-${rev}`} field={f} value={form[f]} original={full[f]} onChange={(v) => setField(f, v)} />)}
            </Section>
          ))}

          <Collapsible className="rounded-3xl border border-dashed border-slate-300 bg-slate-50/50" data-testid="edit-section-advanced">
            <CollapsibleTrigger className="group w-full flex items-center justify-between px-6 py-5 text-left" data-testid="edit-advanced-toggle">
              <div>
                <div className="font-display text-lg font-semibold text-[#071b22]">Advanced</div>
                <div className="text-sm text-slate-500">Linked IDs, system fields and technical data · {advanced.length + PROTECTED_FIELDS.filter((k) => full[k] != null).length} fields</div>
              </div>
              <ChevronDown className="h-5 w-5 text-slate-400 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-6 pb-6 space-y-6">
              <div className="grid gap-5 md:grid-cols-2">
                {advanced.map((f) => <FieldInput key={`${f}-${rev}`} field={f} value={form[f]} original={full[f]} onChange={(v) => setField(f, v)} />)}
                {PROTECTED_FIELDS.filter((k) => full[k] != null && k !== "_id").map((k) => <FieldInput key={k} field={k} value={full[k]} readOnly />)}
              </div>
              <button onClick={() => setShowRaw((s) => !s)} className="inline-flex items-center gap-2 text-sm font-medium text-[#165a71]" data-testid="edit-raw-toggle"><Code2 className="h-4 w-4" /> {showRaw ? "Hide" : "Show"} raw record</button>
              {showRaw && <pre className="max-h-96 overflow-auto rounded-2xl bg-[#071b22] text-[#cfe7ee] p-4 text-xs" data-testid="edit-raw-json">{JSON.stringify(full, null, 2)}</pre>}
            </CollapsibleContent>
          </Collapsible>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6">
          {statusField && meta.status_transitions?.length > 0 && (
            <SideCard title="Status" testid="crud-edit-status-card">
              <select value={form[statusField] || ""} onChange={(e) => changeStatus(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm capitalize outline-none focus:border-[#165a71]" data-testid="crud-edit-status-select">
                {!meta.status_transitions.includes(form[statusField]) && <option value={form[statusField] || ""}>{String(form[statusField] || "—").replace(/_/g, " ")}</option>}
                {meta.status_transitions.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <p className="mt-2 text-xs text-slate-400">Status changes save instantly and are logged.</p>
            </SideCard>
          )}
          {mainGroups.length > 1 && (
            <SideCard title="On this page" testid="crud-edit-toc">
              <nav className="space-y-1">
                {mainGroups.map((g) => <a key={g.key} href={`#sec-${g.key}`} className="block rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-[#165a71]">{g.label}</a>)}
              </nav>
            </SideCard>
          )}
          <SideCard title="Record" testid="crud-edit-info">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">Created</dt><dd className="text-slate-700">{full.created_at ? fmtDate(full.created_at) : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Updated</dt><dd className="text-slate-700">{full.updated_at ? fmtDate(full.updated_at) : "—"}</dd></div>
            </dl>
            <div className="mt-4 pt-4 border-t border-slate-100">
              {full.deleted_at
                ? <button onClick={restore} className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="crud-edit-restore"><RotateCcw className="h-4 w-4" /> Restore record</button>
                : <button onClick={trash} className="inline-flex items-center gap-2 text-sm font-medium text-rose-600" data-testid="crud-edit-trash"><Trash2 className="h-4 w-4" /> Move to trash</button>}
            </div>
          </SideCard>
        </aside>
      </div>

      {dirty && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 rounded-full bg-[#071b22] text-white pl-6 pr-2 py-2 shadow-2xl animate-in fade-in slide-in-from-bottom-4" data-testid="crud-unsaved-bar">
          <span className="text-sm">You have unsaved changes</span>
          <button onClick={() => hydrate(full)} className="rounded-full px-4 py-2 text-sm text-white/70 hover:text-white" data-testid="crud-discard">Discard</button>
          <button onClick={save} disabled={saving} className="rounded-full bg-[#5fd3c6] text-[#071b22] px-5 py-2 text-sm font-semibold disabled:opacity-50" data-testid="crud-save-bar">{saving ? "Saving…" : "Save"}</button>
        </div>
      )}
    </div>
  );
}
